import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from "lucide-react";
import { cn } from "@/lib/cn";

export type ToastTone = "ok" | "info" | "warn" | "danger";

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  /** Auto-dismiss ms; 0 = sticky (e.g. a loud sync-now block the operator should read). */
  durationMs?: number;
}

interface NotifierValue {
  notify: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const NotifierContext = createContext<NotifierValue | null>(null);

const ICON: Record<ToastTone, ReactNode> = {
  ok: <CheckCircle2 className="size-4 text-(--ok)" aria-hidden />,
  info: <Info className="size-4 text-(--info)" aria-hidden />,
  warn: <AlertTriangle className="size-4 text-(--warn)" aria-hidden />,
  danger: <XCircle className="size-4 text-(--danger)" aria-hidden />,
};

export function NotifierProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Announcements live in ALWAYS-MOUNTED regions (a live region created together with its content is
  // unreliably announced). Each is a distinct, keyed node REMOVED ~1s after it's voiced — so identical /
  // repeated messages still re-announce, bursts aren't clobbered, and no stale text lingers in the DOM.
  const [announcements, setAnnouncements] = useState<
    { id: string; msg: string; assertive: boolean }[]
  >([]);
  // Every announcement schedules its own ~1s removal timer (notify() can fire faster than 1s apart, so
  // several are in flight at once). Tracked here so unmount can cancel whatever is still pending —
  // otherwise a dangling one fires into a torn-down tree (in tests: a dead `window` after the jsdom
  // environment for that test file has already been swept, reported as an unhandled error against
  // whichever test happens to be running when the ~1s elapses).
  const announcementTimers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = announcementTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((toast: Omit<Toast, "id">) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...toast, id }]);
    const msg = toast.body ? `${toast.title}. ${toast.body}` : toast.title;
    const assertive = toast.tone === "danger" || toast.tone === "warn";
    const aid = crypto.randomUUID();
    setAnnouncements((prev) => [...prev, { id: aid, msg, assertive }]);
    const timer = setTimeout(() => {
      announcementTimers.current.delete(timer);
      setAnnouncements((prev) => prev.filter((a) => a.id !== aid));
    }, 1000);
    announcementTimers.current.add(timer);
    return id;
  }, []);

  return (
    <NotifierContext.Provider value={{ notify, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} announcements={announcements} />
    </NotifierContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
  announcements,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
  announcements: { id: string; msg: string; assertive: boolean }[];
}) {
  const { t } = useTranslation();
  return (
    <>
      {/* Always-mounted live regions; each announcement is a fresh keyed node (no aria-atomic) removed
          after it's voiced — repeats re-announce, bursts queue, nothing stale lingers. danger/warn go to
          the aria-live="assertive" region (NOT role="alert" — that role belongs to the durable inline
          SyncNowControl panels; a second role="alert" here would double-announce and collide). */}
      <div aria-live="polite" className="sr-only">
        {announcements
          .filter((a) => !a.assertive)
          .map((a) => (
            <p key={a.id}>{a.msg}</p>
          ))}
      </div>
      <div aria-live="assertive" className="sr-only">
        {announcements
          .filter((a) => a.assertive)
          .map((a) => (
            <p key={a.id}>{a.msg}</p>
          ))}
      </div>
      <div
        aria-label={t("a11y.notification")}
        className="pointer-events-none fixed bottom-4 right-4 flex w-full max-w-sm flex-col gap-2"
        style={{ zIndex: "var(--z-toast)" }}
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </div>
    </>
  );
}

/** One visible toast card — presentation only (the announcer above handles SR). Owns its auto-dismiss
 * timer and PAUSES it on hover/focus so a toast can't vanish mid-read; resumes with the time remaining. */
function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { t } = useTranslation();
  const duration = toast.durationMs ?? 5000;
  const remaining = useRef(duration);
  const startedAt = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  const start = useCallback(() => {
    if (duration <= 0 || remaining.current <= 0) return; // 0 = sticky
    startedAt.current = performance.now();
    timer.current = setTimeout(() => onDismiss(toast.id), remaining.current);
  }, [duration, onDismiss, toast.id]);

  useEffect(() => {
    start();
    return clear;
  }, [start, clear]);

  function pause() {
    if (duration <= 0 || !timer.current) return;
    clear();
    remaining.current -= performance.now() - startedAt.current;
  }
  function resume() {
    if (duration <= 0 || timer.current) return;
    if (remaining.current <= 0) {
      onDismiss(toast.id); // pause landed at the edge of expiry — dismiss now rather than strand it
      return;
    }
    start();
  }

  return (
    <div
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocusCapture={pause}
      onBlurCapture={resume}
      className={cn(
        "pointer-events-auto flex items-start gap-3 rounded-(--radius) border border-(--border) " +
          "bg-(--surface-raised) px-3.5 py-3 shadow-(--shadow-lg) " +
          "animate-[toast-in_var(--dur)_var(--ease-out)] motion-reduce:animate-none",
      )}
    >
      <span className="mt-0.5 shrink-0">{ICON[toast.tone]}</span>
      <div className="min-w-0 flex-1">
        <p className="text-(length:--text-sm) font-medium text-(--text)">{toast.title}</p>
        {toast.body && (
          <p className="mt-0.5 text-(length:--text-xs) text-(--text-muted)">{toast.body}</p>
        )}
      </div>
      <button
        type="button"
        aria-label={t("action.close")}
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 rounded-(--radius-sm) p-0.5 text-(--text-subtle) hover:text-(--text) focus-visible:outline-2 focus-visible:outline-(--brand)"
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/** Hook to raise/dismiss toasts. Throws outside the provider (programmer error). */
export function useNotifier(): NotifierValue {
  const ctx = useContext(NotifierContext);
  if (!ctx) throw new Error("useNotifier must be used within <NotifierProvider>");
  return ctx;
}
